import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../../shared/types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
export function PropertyInformation() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data for a Maldivian resort
  const [property, setProperty] = useState<Property>({
    name: 'Baa Atoll Lagoon Paradise Resort',
    description: 'Nestled on the pristine shores of Baa Atoll, Baa Atoll Lagoon Paradise Resort offers a perfect blend of Maldivian luxury and natural beauty. Surrounded by turquoise lagoons and overlooking the crystal-clear waters of the Indian Ocean, this exclusive retreat provides an authentic Maldivian experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Overwater Villas', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Maldivian Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: 'Eydhafushi Island',
      city: 'Baa Atoll',
      postalCode: '06010',
      country: 'Maldives',
      latitude: '5.1042',
      longitude: '73.0680'
    },
    contact: {
      email: 'reservations@baaatolllagoon.mv',
      phone: '+960 660 1234',
      website: 'https://www.baaatolllagoonresort.mv',
      timezone: 'Indian/Maldives'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Maldivian Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Maldivian Massage', 'Bodu Beru Performances', 'Dolphin Cruise Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-9132">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-9133">
            {tag}
            <button aria-label={`Remove ${tag}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-9134">
              <XIcon size={12} aria-hidden="true" data-id="element-9135" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-9136">
      {/* Simplified header section */}
      <div data-id="element-9137">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-9138">
          {t('pages.PropertyInformation.hotel')}
        </h1>
        <div className="flex justify-between items-center" data-id="element-9139">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-9140">
            <div className="flex space-x-6" data-id="element-9141">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-9142">
                {t('pages.PropertyInformation.hotel_information')}
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-9143">
                {t('components.EditRoomForm.media_assets')}
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-9144">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-9145">
              <span className="mr-2" data-id="element-9146">
                {t('pages.PropertyInformation.draft_submitted_for_review')}
              </span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-9147" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-9148">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-9149">
                {t('pages.PropertyInformation.draft_status')}
              </h3>
              <p className="text-xs text-gray-600" data-id="element-9150">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-9151">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9152">
          <div data-id="element-9153">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9154">
              {t('pages.PropertyInformation.hotel_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-9155" />
          </div>
          <div data-id="element-9156">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9157">
              {t('pages.PropertyInformation.designation')}
            </label>
            <div className="relative" data-id="element-9158">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-9159">
                <option value="Resort" data-id="element-9160">
                  {t('pages.PropertyInformation.resort')}
                </option>
                <option value="Hotel" data-id="element-9161">
                  {t('pages.PropertyInformation.hotel')}
                </option>
                <option value="Villa" data-id="element-9162">
                  {t('pages.PropertyInformation.villa')}
                </option>
                <option value="Guesthouse" data-id="element-9163">
                  {t('pages.PropertyInformation.guesthouse')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9164">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9165" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9166">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9167">
            {t('components.AddRoomForm.description')}
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-9168" />
        </div>
        <div className="mb-6" data-id="element-9169">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9170">
            {t('pages.PropertyInformation.star_rating')}
          </label>
          <div className="relative" data-id="element-9171">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-9172">
              <option value="5" data-id="element-9173">5</option>
              <option value="4" data-id="element-9174">4</option>
              <option value="3" data-id="element-9175">3</option>
              <option value="2" data-id="element-9176">2</option>
              <option value="1" data-id="element-9177">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9178">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9179" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9180">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9181">
            {t('pages.PropertyInformation.tags')}
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9182">
          <div data-id="element-9183">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9184">
              {t('pages.PropertyInformation.address')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-9185" />
          </div>
          <div data-id="element-9186">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9187">
              {t('pages.PropertyInformation.area_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Pantai Cenang" data-id="element-9188" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9189">
          <div data-id="element-9190">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9191">
              {t('pages.PropertyInformation.city')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-9192" />
          </div>
          <div data-id="element-9193">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9194">
              {t('pages.PropertyInformation.postal_code')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-9195" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-9196">
          <div data-id="element-9197">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9198">
              {t('pages.PropertyInformation.country')}
            </label>
            <div className="relative" data-id="element-9199">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-9200">
                <option value="Maldives" data-id="element-9201">
                  {t('pages.PropertyInformation.malaysia')}
                </option>
                <option value="Sri Lanka" data-id="element-9202">
                  {t('pages.PropertyInformation.indonesia')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9203">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9204" />
              </div>
            </div>
          </div>
          <div data-id="element-9205">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9206">
              {t('pages.PropertyInformation.latitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-9207" />
          </div>
          <div data-id="element-9208">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9209">
              {t('pages.PropertyInformation.longitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-9210" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9211">
          <div data-id="element-9212">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9213">
              {t('pages.PropertyInformation.contact_email')}
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-9214" />
          </div>
          <div data-id="element-9215">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9216">
              {t('pages.PropertyInformation.phone_number')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-9217" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9218">
          <div data-id="element-9219">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9220">
              {t('pages.PropertyInformation.website')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-9221" />
          </div>
          <div data-id="element-9222">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9223">
              {t('pages.PropertyInformation.timezone')}
            </label>
            <div className="relative" data-id="element-9224">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-9225">
                <option value="Asia/Kuala_Lumpur" data-id="element-9226">
                  {t('pages.PropertyInformation.asiakualalumpur')}
                </option>
                <option value="Asia/Singapore" data-id="element-9227">
                  {t('pages.PropertyInformation.asiasingapore')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9228">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9229" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9230">
          <div data-id="element-9231">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9232">
              {t('pages.PropertyInformation.checkin_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-9233" />
          </div>
          <div data-id="element-9234">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9235">
              {t('pages.PropertyInformation.checkout_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-9236" />
          </div>
        </div>
        <div className="mb-6" data-id="element-9237">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9238">
            {t('pages.PropertyInformation.hotel_amenities')}
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-9239">
            <div className="flex flex-wrap gap-2" data-id="element-9240">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-9241">
                  {amenity}
                  <button aria-label={`Remove ${amenity}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-9242">
                    <XIcon size={12} aria-hidden="true" data-id="element-9243" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-9244">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9245">
            {t('pages.PropertyInformation.transportation')}
          </label>
          <div className="space-y-2" data-id="element-9246">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-9247">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-9248" />

                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-9249">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-9250">
      <polyline points="6 9 12 15 18 9" data-id="element-9251"></polyline>
    </svg>;
}